import type React from "react";

import { Typography } from "@mui/material";
import { addDays } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";
import { useLocalizedDate } from "utils/date";

interface Props {
  dateSigned: string;
  onClose: () => void;
  fileLink: string | null;
  duration: number;
}

export const PlanOfCareCertificationBox: React.FC<Props> = ({ onClose, dateSigned, fileLink, duration }) => {
  const { t } = useTranslation();
  const { parseISO, format } = useLocalizedDate();
  const dateSignedParsed = parseISO(dateSigned);
  const validUntil = addDays(dateSignedParsed, duration);

  return (
    <Container data-testid="plan-of-care-box">
      <TextRow>
        <Typography variant="subtitle1">{t(`patient_header.plan_of_care_box.certified`)}</Typography>
        <Typography variant="subtitle2">{format(dateSignedParsed, "PP")}</Typography>
        <Typography variant="subtitle1">{t(`patient_header.plan_of_care_box.valid_until`)}</Typography>
        <Typography variant="subtitle2">{format(validUntil, "PP")}</Typography>
      </TextRow>

      <ButtonRow>
        {fileLink && (
          <BaseButton
            variant="text"
            size="small"
            text={t("buttons.view")}
            onClick={() => window.open(fileLink, "_blank")}
          />
        )}
        <BaseButton variant="tertiary" size="small" text={t("buttons.dismiss")} onClick={onClose} />
      </ButtonRow>
    </Container>
  );
};

const Container = styled.div`
  background: ${props => props.theme.colors.confirmation_green};
  border-radius: ${props => props.theme.borderRadius.basic};
  padding: 12px ${props => props.theme.spacing.S_20};
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
  gap: 8px;
  margin-bottom: 24px;
`;

const TextRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  flex: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;
